import { BrandMark } from '@/shared/components/BrandMark';
import { messages } from '@/shared/constants/messages';

/**
 * O lado institucional do login: o papel azul da IBFT com a frase de visão da
 * própria igreja, sangrado até a borda da tela.
 *
 * **Ele não é enfeite — é a credencial da tela.** O login é a única página que
 * abre sem sessão, e o voluntário chega nela por um link recebido no WhatsApp.
 * Reconhecer a peça que ele já viu no Instagram da igreja é o que responde "é
 * o painel da IBFT?" antes de existir qualquer campo para digitar.
 *
 * **Muda de papel conforme a largura, não de conteúdo.** A partir de `lg` é
 * meia tela, com as três lâminas empilhadas como na arte original. No celular
 * vira uma faixa curta no topo, com a frase em uma linha só: a tela de 375px
 * pertence ao campo de e-mail (RNF-U02), e uma capa de 260px empurraria a
 * tarefa para baixo da dobra. As lâminas são o que sai — a frase, o papel e a
 * marca ficam.
 *
 * **Três pontos de ancoragem na coluna alta**: marca no topo, visão no centro,
 * igreja no rodapé. É a diagramação de cartaz, e é o que a peça original faz.
 * Ancorar tudo embaixo (como no cartão menor que isto substituiu) deixava 400px
 * de papel vazio no topo de uma tela de 900px.
 *
 * **O `BrandMark` entra direto no papel, sem disco atrás.** Ele é um círculo
 * laranja e o papel agora é azul: a própria borda do disco já separa a marca do
 * fundo, e o anel branco que existia aqui só fazia falta enquanto o papel era
 * laranja — ali a marca encostava numa cor quase igual à dela e sumia. Se o
 * papel um dia voltar a ser laranja, o disco branco volta junto.
 */
export function LoginVisionPanel() {
  const { brand, church, visionEyebrow, visionLead, visionLines, visionBridge } = messages.auth;

  return (
    <aside className="bg-vision-paper flex flex-col p-6 text-white sm:p-8 lg:p-12 xl:p-16">
      <div className="flex items-center gap-3">
        <BrandMark className="size-9 shrink-0" />
        <span className="text-sm font-semibold tracking-tight">{brand}</span>
      </div>

      {/* `max-w-xl` porque meia tela de um monitor largo passa de 900px, e uma
          linha de texto com essa medida não se lê — ela se varre. */}
      <div className="mt-6 max-w-xl lg:mt-0 lg:flex lg:flex-1 lg:flex-col lg:justify-center lg:py-10">
        <p className="font-serif text-sm italic text-white lg:text-base">{visionEyebrow}</p>

        <p className="mt-1 font-serif text-lg leading-snug tracking-tight sm:text-xl lg:mt-2 lg:text-[2.125rem] xl:text-[2.5rem]">
          {visionLead}{' '}
          {/* Celular: a frase inteira corrida. As lâminas viriam em três blocos
              de papel dentro de uma faixa de 130px — colagem não cabe aqui. */}
          <span className="lg:hidden">{visionLines.join(' ')}</span>
        </p>

        <ul className="mt-4 hidden lg:block">
          {visionLines.map((line, index) => (
            /* O `<li>` é o que quebra a linha; a lâmina é o `<span>` dentro
               dele, que encolhe até o texto. Com a lâmina no próprio `<li>`
               como `inline-block`, as três voltam a caber lado a lado e a
               colagem vira uma frase corrida com fundo. */
            <li key={line} className="mt-2">
              <span
                className="vision-slat inline-block rounded-[0.2rem] px-4 py-1.5 font-serif text-[2.125rem] leading-tight tracking-tight xl:text-[2.5rem]"
                /* Cada lâmina entra um pouco à direita da anterior, como na
                   arte — o empilhamento é levemente torto de propósito. */
                style={{ marginLeft: `${index * 1.25}rem` }}
              >
                {line}
              </span>
            </li>
          ))}
        </ul>

        {/* Some no celular: a faixa azul ali é cabeçalho de uma tela cujo
            assunto é o campo de e-mail (RNF-U02). Três linhas a mais empurram
            a tarefa para baixo da dobra num aparelho de 375×812. */}
        <p className="mt-10 hidden max-w-md text-sm leading-relaxed text-white lg:block">
          {visionBridge}
        </p>
      </div>

      {/* Sem `lg:mt-auto` explícito: o bloco do meio é `flex-1` e já empurra
          esta linha para o rodapé da coluna. */}
      <p className="mt-6 border-t border-white/25 pt-4 text-xs text-white lg:mt-0 lg:pt-5">
        {church}
      </p>
    </aside>
  );
}
